import { h, Component } from 'preact';
import { joinClasses } from 'utils/utils';
import pure from 'utils/pure';
import watch from 'utils/watch';
import { smoothScrollBy } from 'utils/smoothScroll';
import s from 'components/shared/fakeScrollbar/fakeScrollbar.sss';

// TODO how to detect scrollHeight changes ?
export default @pure class FakeScrollbar extends Component {

	constructor() {
		super();
		this.refresh = this.refresh.bind(this);
		this.mouseDown = this.mouseDown.bind(this);
		this.mouseMove = this.mouseMove.bind(this);
		this.mouseUp = this.mouseUp.bind(this);
		this.start = this.start.bind(this);
		this.stop = this.stop.bind(this);
	}

	componentDidMount() {
		this.init();
	}

	componentDidUpdate(props) {
		this.container = this.base;
		this.thumb = this.container.firstChild;

		if (props.realScrollContainer !== this.props.realScrollContainer) {
			this.updateScroller(this.props.realScrollContainer);
		}

		// Going to another page unmounts the component (and calls stop)
		// But coming back to the page doesn't trigger componentDidMount again
		// So we have to make sure it's running by calling start (even if active === this.props.active)
		// Might not be necessary anymore but needs testing
		this[this.props.active ? 'start' : 'stop']();
	}

	componentWillUnmount() {
		this.stop();
	}

	init() {
		this.container = this.base;
		this.thumb = this.container.firstChild;
		this.updateScroller(this.props.realScrollContainer);
		if (!this.scroller) return;
		// Issue : we don't know when the content changes (and so when the scrollHeight changes)
		// We watch properties with a timer instead (a bit intensive, would be great to improve that if possible ! e.g. using events)
		if (this.props.active) {
			this.start();
		}
	}

	updateScroller(element) {
		let doc = document;
		// Detect correct scrolling element (either body or documentElement)
		if (!element || element === doc.body) {
			element = doc.scrollingElement || (
				doc.documentElement.scrollHeight > doc.body.scrollHeight && doc.compatMode.startsWith('CSS1') ? doc.documentElement : doc.body
			);
		}
		this.scroller = element;
		if (this.started) {
			this.updateWatch();
		}
	}

	updateWatch() {
		this.watch?.remove();
		this.watch = watch(this.scroller, ['scrollTop', 'scrollHeight', 'clientHeight'], this.refresh);
	}

	start() {
		if (this.started) return;
		this.started = true;

		this.updateWatch();

		this.container.addEventListener('mousedown', this.mouseDown);
		window.addEventListener('mousemove', this.mouseMove);
		window.addEventListener('mouseup', this.mouseUp);
	}

	stop() {
		if (!this.started) return;
		this.started = false;

		if (this.watch) {
			this.watch.remove();
			this.watch = null;
		}

		this.container.removeEventListener('mousedown', this.mouseDown);
		window.removeEventListener('mousemove', this.mouseMove);
		window.removeEventListener('mouseup', this.mouseUp);
	}

	mouseDown(event) {
		event.preventDefault();

		const thumbPos = this.thumb.getBoundingClientRect();

		if (event.target === this.thumb) {
			this.grabbedAt = (event.clientY - thumbPos.top) / thumbPos.height;
			this.container.className += ' ' + s.grabbed;
			// We refresh manually while grabbing
			if (this.watch) this.watch.stop();
			return;
		}

		// Good variable name. (best when equals to baseSize from refresh)
		const offThumbClickScrollQuantityFactor = .3;
		const scrollBy = (event.clientY < thumbPos.top ? -1 : 1) * this.scroller.clientHeight * offThumbClickScrollQuantityFactor;

		// Smooth scrolling
		smoothScrollBy(this.scroller, scrollBy, 150);
	}

	mouseMove(event) {
		if (this.grabbedAt != null) {
			const thumbPos = this.thumb.getBoundingClientRect();
			const thumbHeight = thumbPos.bottom - thumbPos.top;
			const desired = event.clientY - (this.grabbedAt * thumbHeight);

			const containerPos = this.container.getBoundingClientRect();
			const containerHeight = containerPos.bottom - containerPos.top;
			const max = this.scroller.scrollHeight - this.scroller.clientHeight;
			const newScroll = Math.round(max * (desired - containerPos.top) / (containerHeight - thumbHeight));

			if (newScroll !== this.scroller.scrollTop) {
				this.scroller.scrollTop = newScroll;
				// Make sure we refresh (as we use the thumb current position to find the next one)
				this.refresh();
			}
		}
	}

	mouseUp() {
		if (this.grabbedAt != null) {
			this.grabbedAt = null;
			this.container.className = (' ' + this.container.className + ' ').replace(' ' + s.grabbed + ' ', ' ').slice(1, -1);
			// Restart the automatic refreshing
			if (this.watch) this.watch.start();
		}
	}

	refresh() {
		if (!this.started) return;

		// Can be expensive to compute the body height if the dom tree is big
		const pos = this.scroller.scrollTop;
		const max = this.scroller.scrollHeight - this.scroller.clientHeight;
		const maxPage = max / this.scroller.clientHeight;

		// Should this be a prop option ?
		let visible = max !== 0;
		if (visible !== this.visible) {
			this.visible = visible;
			this.container.style.display = visible ? '' : 'none';
		}

		// These things affect the size of the thumb (minSize is also affected by min-height in the .sss)
		// Default (similar to browser size) is 1, 1, 0
		const baseSize = .3;
		const maxPageAffect = .5;
		const minSize = .1;
		const size = Math.max(baseSize / (maxPageAffect * maxPage + 1), minSize);
		const p = pos / max;
		this.thumb.style.height = (100 * size) + '%';
		this.thumb.style.top = (100 * p) + '%';
		this.thumb.style.transform = 'translateY(' + (-100 * p) + '%)';

		let oldRect;
		let newRect;
		if (this.grabbedAt != null) {
			oldRect = this.thumb.getBoundingClientRect();
		}

		let cBottom = 0;
		if (this.base.parentElement) {
			const pBounds = this.base.parentElement.getBoundingClientRect();
			// Ensure the min height to a full page (45 is the header height)
			// Todo handle that better. Maybe hide the scrollbar if pbounds.height < document.body.clientHeight ?
			const top = this.props.top ?? 45;
			let bottom = this.props.bottom ?? 0;
			bottom = Math.max(pBounds.bottom, pBounds.top + document.body.clientHeight - top - bottom);
			if (bottom < document.body.clientHeight) {
				cBottom = (document.body.clientHeight - bottom);
			}
		}
		this.container.style.bottom = cBottom + 'px';

		if (this.grabbedAt != null) {
			// Make it smooth when the size of the container is changing
			// Not the best when grabbing it reduced (footer showing) and dragging up (to go back to the top)
			// But how to do better ?
			newRect = this.thumb.getBoundingClientRect();
			const mousePos = oldRect.top + this.grabbedAt * (oldRect.bottom - oldRect.top);
			this.grabbedAt = (mousePos - newRect.top) / (newRect.bottom - newRect.top);
		}
	}

	render({ active }) {
		return (
			<div class={joinClasses(s.track, (!active && s.hidden), this.props.class)}>
				<div class={s.thumb} />
			</div>
		);
	}

}
